import React, { useEffect, useRef, useState } from "react";
import { FaLock } from "react-icons/fa6";
import { getEmailSuggestion } from "../utils/emailSuggestion";
import EmailSuggestion from "./EmailSuggestion";
import { GATES, isQuoteRepairEnabled, trackEvent } from "../utils/statsig";
import "../styles/direct-request.css";

// Direct arm of the direct-to-brand vs Cabana split test: after the address,
// a short request form that goes to the brand. No price, tier or quote is
// shown anywhere on this screen.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function phoneDigits(value) {
  const d = String(value || "").replace(/\D/g, "");
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
}

function formatPhone(value) {
  const d = String(value || "").replace(/\D/g, "").slice(0, 11);
  const local = d.length === 11 && d.startsWith("1") ? d.slice(1) : d.slice(0, 10);
  if (local.length < 4) return local;
  if (local.length < 7) return `(${local.slice(0, 3)}) ${local.slice(3)}`;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6, 10)}`;
}

export function validateDirectRequest(values) {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = "Please enter your first name";
  if (!values.lastName.trim()) errors.lastName = "Please enter your last name";
  if (phoneDigits(values.phone).length !== 10) errors.phone = "Please enter a valid 10-digit phone number";
  if (!values.email.trim()) errors.email = "Please enter your email address";
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = "Please enter a valid email address";
  return errors;
}

const FIELD_ORDER = ["firstName", "lastName", "phone", "email"];

const DirectRequest = ({ assignment, initial = {}, address = "", onSubmit, onBusiness }) => {
  const brandName = assignment?.brand_display_name || "";
  const brand = brandName || "Our team";
  const plans = Array.isArray(assignment?.plans) ? assignment.plans : [];
  const [propertyDetails] = useState(() => isQuoteRepairEnabled(GATES.DIRECT_PROPERTY_DETAILS));
  const [values, setValues] = useState({
    firstName: initial.firstName || "",
    lastName: initial.lastName || "",
    phone: formatPhone(initial.phone || ""),
    email: "",
    plans: [],
    notes: "",
    poolSize: "",
    poolCondition: "",
    poolOption: "",
    trees: false,
    service: "",
  });
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // Required first question. Only "home" continues to the direct form;
  // "business" hands back to the normal commercial funnel.
  const [propertyType, setPropertyType] = useState("");
  const [propertyError, setPropertyError] = useState(false);
  const inFlight = useRef(false);
  const typoAck = useRef(false);
  const headingRef = useRef(null);
  const thanksRef = useRef(null);

  useEffect(() => {
    // Move focus to the new screen's heading so keyboard and screen reader
    // users land at the start of the form, and again on the thank-you view.
    // preventScroll: the header is fixed, so a scrolled focus would tuck
    // the heading under it.
    (submitted ? thanksRef : headingRef).current?.focus({ preventScroll: true });
  }, [submitted]);

  useEffect(() => {
    setErrors(validateDirectRequest(values));
  }, [values]);

  useEffect(() => {
    // The header progress bar normally reads the active Swiper slide; this
    // screen replaces the Swiper, so set it directly.
    const bar = document.querySelector(".form-progress-slider");
    if (bar) bar.style.width = submitted ? "100%" : "60%";
  }, [submitted]);

  const update = (name, value) => {
    setValues(prev => ({ ...prev, [name]: name === "phone" ? formatPhone(value) : value }));
    if (submitError) setSubmitError("");
    if (name === "email") { setEmailSuggestion(null); typoAck.current = false; }
  };

  const togglePlan = (label, checked) => {
    setValues(prev => ({
      ...prev,
      plans: checked ? [...prev.plans.filter(p => p !== label), label] : prev.plans.filter(p => p !== label),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    if (!propertyType) {
      setPropertyError(true);
      document.getElementById("direct-property-home")?.focus();
      return;
    }
    if (propertyType === "business") {
      onBusiness?.();
      return;
    }
    const current = validateDirectRequest(values);
    if (Object.keys(current).length) {
      setErrors(current);
      setShowErrors(true);
      const first = FIELD_ORDER.find(k => current[k]);
      document.getElementById(`direct-${first}`)?.focus();
      return;
    }
    const suggestion = getEmailSuggestion(values.email.trim());
    if (suggestion && !typoAck.current) {
      setEmailSuggestion(suggestion);
      typoAck.current = true;
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      await onSubmit({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: values.phone,
        email: values.email.trim(),
        plans: plans.map(p => p.label).filter(label => values.plans.includes(label)),
        notes: values.notes.trim(),
        ...(propertyDetails ? {
          poolSize: values.poolSize,
          poolCondition: values.poolCondition,
          poolOption: values.poolOption,
          poolOption1: values.trees ? ["trees_over_pool"] : [],
          service: values.service ? [values.service] : [],
        } : {}),
      });
      trackEvent('split_direct_request_completed', { property_details: String(propertyDetails) });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (_) {
      setSubmitError(assignment?.phone_display
        ? `We could not send your request. Please try again, or call ${assignment.phone_display}.`
        : "We could not send your request. Please try again.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="form-container direct-request">
        <div className="direct-thanks">
          <img src="/images/thank-you.svg" alt="" className="direct-thanks-image" />
          <h1 tabIndex={-1} ref={thanksRef}>Thank you!</h1>
          <p className="direct-thanks-message">{brand} will reach out shortly.</p>
          {assignment?.phone_display && assignment?.phone_e164 && (
            <p className="direct-thanks-message">
              Questions in the meantime? Call{" "}
              <a href={`tel:${assignment.phone_e164}`} data-split-phone="thanks">{assignment.phone_display}</a>.
            </p>
          )}
        </div>
      </div>
    );
  }

  const fieldClass = (name) => `form-control input-field ${showErrors && errors[name] ? "invalid-field" : ""}`;
  const errorFor = (name) => showErrors && errors[name]
    ? <div className="field-error-message" id={`direct-${name}-error`}>{errors[name]}</div>
    : null;
  const described = (name) => (showErrors && errors[name] ? `direct-${name}-error` : undefined);

  return (
    <div className="form-container direct-request">
      <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>
        <div className="step-section">
          <h1 tabIndex={-1} ref={headingRef}>{brandName ? `Request service from ${brandName}` : "Request pool service"}</h1>
          <div className="subtitle">
            <span>Tell us how to reach you and {brandName || "our team"} will contact you to set up service.</span>
          </div>
          {address && <p className="direct-address">Service address: {address}</p>}
        </div>
        <fieldset className="direct-plans direct-property checkbox-section-mini"
          aria-describedby={propertyError ? "direct-property-error" : undefined}>
          <legend>Is this pool at a home or a business?</legend>
          {[["home", "Home"], ["business", "Business"]].map(([value, label]) => (
            <div className={`border-slide5 ${propertyError ? "invalid-field" : ""}`} key={value}>
              <div className="form-check p-3">
                <input className="form-check-input" type="radio" name="direct-property"
                  id={`direct-property-${value}`} value={value} checked={propertyType === value}
                  aria-invalid={propertyError}
                  onChange={() => { setPropertyType(value); setPropertyError(false); }} />
                <label className="form-check-label checkbox" htmlFor={`direct-property-${value}`}>{label}</label>
              </div>
            </div>
          ))}
          {propertyError && (
            <div className="field-error-message" id="direct-property-error">Please choose home or business</div>
          )}
        </fieldset>
        {propertyType === "home" && <div className="field-mini-wrap">
          <div className="row">
            <div className="col-md-6">
              <div className="input-container form-floating position-relative input-box">
                <input className={fieldClass("firstName")} placeholder="First name" id="direct-firstName"
                  name="firstName" autoComplete="given-name" value={values.firstName}
                  onChange={e => update("firstName", e.target.value)}
                  aria-invalid={showErrors && !!errors.firstName} aria-describedby={described("firstName")} />
                <label htmlFor="direct-firstName">First name</label>
              </div>
              {errorFor("firstName")}
            </div>
            <div className="col-md-6">
              <div className="input-container form-floating position-relative input-box">
                <input className={fieldClass("lastName")} placeholder="Last name" id="direct-lastName"
                  name="lastName" autoComplete="family-name" value={values.lastName}
                  onChange={e => update("lastName", e.target.value)}
                  aria-invalid={showErrors && !!errors.lastName} aria-describedby={described("lastName")} />
                <label htmlFor="direct-lastName">Last name</label>
              </div>
              {errorFor("lastName")}
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="input-container form-floating position-relative input-box">
                <input className={fieldClass("phone")} placeholder="Phone number" id="direct-phone"
                  name="phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone}
                  onChange={e => update("phone", e.target.value)}
                  aria-invalid={showErrors && !!errors.phone} aria-describedby={described("phone")} />
                <label htmlFor="direct-phone">Phone number</label>
              </div>
              {errorFor("phone")}
            </div>
            <div className="col-md-6">
              <div className="input-container form-floating position-relative input-box">
                <input className={fieldClass("email")} placeholder="Email" id="direct-email"
                  name="email" type="email" inputMode="email" autoComplete="email" value={values.email}
                  onChange={e => update("email", e.target.value)}
                  onBlur={e => setEmailSuggestion(getEmailSuggestion(e.target.value))}
                  aria-invalid={showErrors && !!errors.email} aria-describedby={described("email")} />
                <label htmlFor="direct-email">Email</label>
              </div>
              {errorFor("email")}
              <EmailSuggestion suggestion={emailSuggestion} onAccept={s => { update("email", s); setEmailSuggestion(null); }} />
            </div>
          </div>
          {plans.length > 0 && (
            <fieldset className="direct-plans checkbox-section-mini">
              <legend>Which weekly plans interest you? <span className="direct-optional">(optional)</span></legend>
              {plans.map(plan => (
                <div className="border-slide5" key={plan.key}>
                  <div className="form-check p-3">
                    <input className="form-check-input" type="checkbox" id={`direct-plan-${plan.key}`}
                      checked={values.plans.includes(plan.label)}
                      onChange={e => togglePlan(plan.label, e.target.checked)} />
                    <label className="form-check-label checkbox" htmlFor={`direct-plan-${plan.key}`}>{plan.label}</label>
                  </div>
                </div>
              ))}
            </fieldset>
          )}
          {propertyDetails && <fieldset className="direct-plans">
            <legend>A little about your pool <span className="direct-optional">(optional)</span></legend>
            <p>Share what you know. We can cover the rest when we talk.</p>
            {[
              ['service', 'What service do you need?', [
                ['regular_pool_spa_service', 'Regular pool or spa service'],
                ['equipment_repair', 'Equipment upgrade or repair'],
                ['filter_or_salt_cell_cleaning', 'Filter or salt cell cleaning'],
                ['greentoclean', 'Green-to-clean pool rescue'],
                ['something_else', 'Something else'],
              ]],
              ['poolSize', 'Approximate pool size', [
                ['Under 10,000 gallons', 'Small (under 10,000 gallons)'],
                ['10,000 to 20,000 gallons', 'Medium (10,000 to 20,000 gallons)'],
                ['Over 20,000 gallons', 'Large (over 20,000 gallons)'],
              ]],
              ['poolCondition', 'How does the water look?', [
                ['crystal_clear', 'Crystal clear'], ['slightly_cloudy', 'Slightly cloudy'],
                ['cloudy_light_green', 'Cloudy and light green'], ['green', 'Green'], ['swamp', 'Swamp'],
              ]],
              ['poolOption', 'Pool or spa?', [
                ['pool_only', 'Pool only'], ['pool_spa', 'Pool with attached spa'],
                ['pool_hot_tub', 'Pool with separate hot tub'], ['hot_tub', 'Hot tub only'],
              ]],
            ].map(([name, label, options]) => <div className="input-box" key={name}>
              <label htmlFor={`direct-${name}`}>{label}</label>
              <select className="form-select" id={`direct-${name}`} value={values[name]}
                onChange={e => update(name, e.target.value)}>
                <option value="">Not sure / skip</option>
                {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
              </select>
            </div>)}
            <div className="form-check p-3">
              <input className="form-check-input" type="checkbox" id="direct-trees"
                checked={values.trees} onChange={e => update('trees', e.target.checked)} />
              <label className="form-check-label" htmlFor="direct-trees">Trees hang over the pool</label>
            </div>
          </fieldset>}
          <div className="row">
            <div className="col-md-12">
              <div className="input-container form-floating position-relative input-box">
                <textarea className="form-control input-field direct-notes" placeholder="Notes"
                  id="direct-notes" name="notes" maxLength={2000} value={values.notes}
                  onChange={e => update("notes", e.target.value)} />
                <label htmlFor="direct-notes">Notes (optional)</label>
              </div>
            </div>
          </div>
        </div>}
        {submitError && <div className="field-error-message direct-submit-error" role="alert">{submitError}</div>}
        <div className="lock-bg">
          <FaLock className="lock-color" />
          <span className="from-lock">Your information is fully secure.</span>
        </div>
        <button type="submit" className="btn btn-next" disabled={submitting}>
          {submitting ? "Sending..." : propertyType === "home" ? "Send request" : "Next"}
        </button>
      </form>
    </div>
  );
};

export default DirectRequest;
